import { Editor, type EditorProps, type Monaco } from '@monaco-editor/react'
import type { editor } from 'monaco-editor'
import * as React from 'react'

type HmqcEditor = editor.IStandaloneCodeEditor

export function ReactEditor(props: Readonly<EditorProps>) {
  const editorRef = React.useRef<HmqcEditor | null>(null)
  const monacoRef = React.useRef<Monaco | null>(null)
  const [height, setHeight] = React.useState<string | number>(0)

  function handleEditorDidMount(editor: HmqcEditor, monaco: Monaco) {
    editorRef.current = editor
    monacoRef.current = monaco
    updateEditorHeight()
  }

  function updateEditorHeight() {
    if (editorRef.current && monacoRef.current) {
      const lineHeight = editorRef.current.getOption(
        monacoRef.current.editor.EditorOption.lineHeight,
      )
      const lineCount = editorRef.current.getModel()?.getLineCount()
      if (lineCount) {
        const newHeight = Math.max(lineHeight * lineCount, 40) // Adds padding
        editorRef.current.layout()

        const cappedHeight =
          'calc(100vh - var(--nav-bar-height) - 2 * var(--standard-padding) - 2rem)' // 2rem is H2 line-height
        setHeight(newHeight < 800 ? newHeight : cappedHeight)
      }
    }
  }

  React.useEffect(() => {
    updateEditorHeight()
  })

  return (
    <div
      style={{
        border: '1px solid var(--color-border-base)',
        backgroundColor: 'var(--chakra-colors-white)',
        borderRadius: '10px',
        //   Note: left padding is removed on purpose, the editor already has enough spacing there
        padding: '0.5rem 0.5rem 0.5rem 0',
      }}
    >
      <Editor
        height={height}
        {...props}
        onMount={handleEditorDidMount}
        options={{
          scrollbar: {
            vertical: 'hidden',
            horizontal: 'hidden',
            alwaysConsumeMouseWheel: false,
          },
          minimap: { enabled: false },
          overviewRulerLanes: 0,
          folding: true,
          scrollBeyondLastColumn: 0,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          fontSize: 16,
          ...props.options,
        }}
      />
    </div>
  )
}
